import Category from '@mui/icons-material/Category';
import Groups from '@mui/icons-material/Groups';
import Translate from '@mui/icons-material/Translate';
import Tune from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import type { ComponentType, SyntheticEvent } from 'react';
import { useId, useState } from 'react';
import AppLayout from '../../components/AppLayout/AppLayout';
import AgeGroupList from './lists/AgeGroupList';
import BookLanguageList from './lists/BookLanguageList';
import CategoryList from './lists/CategoryList';
import OtherSettingsList from './lists/OtherSettingsList';

type SettingsTabId = 'categories' | 'ageGroups' | 'language' | 'other';

const TABS: {
  id: SettingsTabId;
  label: string;
  icon: ComponentType<{ sx?: object }>;
  Panel: ComponentType;
}[] = [
  { id: 'categories', label: 'Категори', icon: Category, Panel: CategoryList },
  { id: 'ageGroups', label: 'Насны ангилал', icon: Groups, Panel: AgeGroupList },
  { id: 'language', label: 'Хэл', icon: Translate, Panel: BookLanguageList },
  { id: 'other', label: 'Бусад', icon: Tune, Panel: OtherSettingsList },
];

function tabIndexFromId(id: SettingsTabId): number {
  return TABS.findIndex((t) => t.id === id);
}

export default function Settings() {
  const baseId = useId();
  const [active, setActive] = useState<SettingsTabId>('categories');
  const tabIndex = tabIndexFromId(active);

  const handleTabsChange = (_event: SyntheticEvent, nextIndex: number) => {
    const next = TABS[nextIndex];
    if (next) setActive(next.id);
  };

  return (
    <AppLayout>
      <Box
        sx={{
          flex: 1,
          py: 4,
          px: { xs: 2, sm: 4 },
          mx: 'auto',
          width: 1,
        }}
      >
        <Box component="header" sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
            Тохиргоо
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabsChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Тохиргооны хэсгүүд"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tab
                  key={tab.id}
                  id={`${baseId}-tab-${tab.id}`}
                  aria-controls={`${baseId}-panel-${tab.id}`}
                  icon={<Icon sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                  label={tab.label}
                  sx={{ minHeight: 48, textTransform: 'none' }}
                />
              );
            })}
          </Tabs>
        </Box>

        <Box
          component="section"
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            p: { xs: 2, sm: 3 },
            minHeight: (theme) => theme.spacing(24),
          }}
        >
          {TABS.map((tab, index) => {
            const Panel = tab.Panel;
            return (
              <Box
                key={tab.id}
                role="tabpanel"
                id={`${baseId}-panel-${tab.id}`}
                aria-labelledby={`${baseId}-tab-${tab.id}`}
                hidden={tabIndex !== index}
              >
                {tabIndex === index && <Panel />}
              </Box>
            );
          })}
        </Box>
      </Box>
    </AppLayout>
  );
}
